import { FC, useRef, useState } from 'react';
import { useRouteMatch } from 'react-router';
import { toast } from 'react-toastify';

import { Typography } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import { GridColDef, GridRowData } from '@material-ui/data-grid';
import AddIcon from '@material-ui/icons/Add';
import BlockIcon from '@material-ui/icons/Block';
import CheckIcon from '@material-ui/icons/Check';
import EmailIcon from '@material-ui/icons/Email';
import EyeIcon from '@material-ui/icons/RemoveRedEye';
import ConfirmDialog from 'src/components/ConfirmDialog';
import { ConfirmDialogRef } from 'src/components/ConfirmDialog/interfaces';
import FilterDrawer from 'src/components/FilterDrawer';
import { FilterDrawerRef } from 'src/components/FilterDrawer/interfaces';
import LinkButton from 'src/components/LinkButton';
import Table from 'src/components/Table';
import TableActions from 'src/components/Table/Actions';
import CustomMenuItem from 'src/components/Table/CustomMenuItem';
import StatusIcon from 'src/components/Table/StatusIcon';
import { columnDateTime } from 'src/components/Table/types';
import usePagination from 'src/hooks/usePagination';
import { ICollaboratorScholarship } from 'src/interfaces/models/IPerson';
import api from 'src/services/api';
import { handleApiResponseErrors } from 'src/utils/errors';
import masks from 'src/utils/masks';
import CollaboratorScholarshipFilterSchema from 'src/validators/CollaboratorScholarship/filter.schema';

import Filters from './Filters';
import { Container } from './styles';

const List: FC = () => {
  const filterRef = useRef<FilterDrawerRef>(null);
  const beneficiaryDialogRef = useRef<ConfirmDialogRef>(null);
  const mailDialogRef = useRef<ConfirmDialogRef>(null);
  const approveDialogRef = useRef<ConfirmDialogRef>(null);
  const rejectDialogRef = useRef<ConfirmDialogRef>(null);
  const [
    selectedScholarship,
    setSelectedScholarship,
  ] = useState<ICollaboratorScholarship | null>(null);
  const [loading, setLoading] = useState(false);
  const { url } = useRouteMatch();
  const {
    handlePageChange,
    handlePageSizeChange,
    data,
    loading: paginationLoading,
    info,
    loadData,
  } = usePagination<ICollaboratorScholarship>(
    'admin/collaborator-scholarships',
  );

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID' },
    {
      field: 'person.name',
      headerName: 'Pessoa',
      flex: 1,
      valueGetter({ row }) {
        return row.person?.name;
      },
    },
    {
      field: 'name',
      headerName: 'Aluno',
      flex: 1,
      renderCell({ row }) {
        return (
          <div>
            <Typography variant="body2">{row.name}</Typography>
            <Typography variant="body2">{masks.cpf(row.document)}</Typography>
          </div>
        );
      },
    },
    {
      field: 'beneficiary.display_name',
      headerName: 'Beneficiária & Unidade',
      width: 210,
      renderCell({ row }) {
        return (
          <div>
            <Typography variant="body2">
              {row.beneficiary?.display_name}
            </Typography>
            <Typography variant="body2">{row.unity?.display_name}</Typography>
          </div>
        );
      },
    },
    {
      field: 'partner.display_name',
      headerName: 'Parceira & Serviço',
      flex: 1,
      minWidth: 200,
      renderCell({ row }) {
        return (
          <div>
            <Typography variant="body2">{row.partner?.display_name}</Typography>
            <Typography variant="body2">
              {row.service?.name} ({row.teachForm?.name})
            </Typography>
          </div>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Criado Em',
      ...columnDateTime,
    },
    {
      field: 'status',
      headerName: 'Status',
      headerAlign: 'center',
      align: 'center',
      renderCell(params) {
        return <StatusIcon status={params.row.status} />;
      },
    },
    {
      field: '',
      disableColumnMenu: true,
      sortable: false,
      align: 'center',
      renderCell({ id, row }) {
        return (
          <TableActions
            resource="Bolsa"
            resourceStatus={row.status}
            editLink={`${url}/${id}`}
            deleteApiUrl={`/admin/collaborator-scholarships/${id}`}
            restoreApiUrl={`/admin/collaborator-scholarships/${id}/restore`}
            restoreStatuses={['inactive', 'rejected']}
            descriptionPrefix={`A bolsa do(a) ${row.name}`}
            onFinish={loadData}
            startItems={
              <>
                {row.status === 'pending' && (
                  <>
                    <CustomMenuItem
                      Icon={CheckIcon}
                      iconProps={{ color: 'primary' }}
                      text="Aprovar"
                      onClick={() => handleApproveDialog(row)}
                    />
                    <CustomMenuItem
                      Icon={BlockIcon}
                      iconProps={{ color: 'secondary' }}
                      text="Negar"
                      onClick={() => handleRejectDialog(row)}
                    />
                    <CustomMenuItem
                      Icon={EmailIcon}
                      iconProps={{ color: 'primary' }}
                      text="Pedir confirmação à Beneficiária"
                      onClick={() => {
                        setSelectedScholarship(row as ICollaboratorScholarship);
                        beneficiaryDialogRef.current?.show();
                      }}
                    />
                  </>
                )}
                {row.status === 'active' && (
                  <>
                    <CustomMenuItem
                      Icon={EyeIcon}
                      iconProps={{ color: 'primary' }}
                      text="Ver Voucher"
                      onClick={() => {
                        window.open(row.voucher_url, '_blank');
                      }}
                    />
                    <CustomMenuItem
                      Icon={EmailIcon}
                      iconProps={{ color: 'primary' }}
                      text="Enviar Voucher"
                      onClick={() => {
                        setSelectedScholarship(row as ICollaboratorScholarship);
                        mailDialogRef.current?.show();
                      }}
                    />
                  </>
                )}
              </>
            }
          />
        );
      },
    },
  ];

  const handleBeneficiaryConfirm = async () => {
    try {
      setLoading(true);

      await api.post(
        `/admin/collaborator-scholarships/${selectedScholarship?.id}/beneficiary-check`,
      );
      toast.success(
        `Pedido enviado à ${selectedScholarship?.beneficiary?.display_name}!`,
      );
      beneficiaryDialogRef.current?.hide();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro ao enviar pedido.');
    } finally {
      setLoading(false);
    }
  };

  const handleMailConfirm = async () => {
    try {
      setLoading(true);

      await api.post(
        `/admin/collaborator-scholarships/${selectedScholarship?.id}/send-mail`,
      );
      toast.success(`E-mail enviado!`);
      mailDialogRef.current?.hide();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro ao enviar e-mail.');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveDialog = (gridRow: GridRowData) => {
    setSelectedScholarship(
      data.find((personSub) => personSub.id == gridRow.id) || null,
    );
    approveDialogRef.current?.show();
  };

  const handleApproveConfirm = async () => {
    try {
      setLoading(true);

      await api.post(
        `/admin/collaborator-scholarships/${selectedScholarship?.id}/approve`,
      );
      toast.success(`Bolsa aprovada!`);
      approveDialogRef.current?.hide();
      loadData();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro ao aprovar bolsa.');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectDialog = (gridRow: GridRowData) => {
    setSelectedScholarship(
      data.find((personSub) => personSub.id == gridRow.id) || null,
    );
    rejectDialogRef.current?.show();
  };

  const handleRejectConfirm = async () => {
    try {
      setLoading(true);

      await api.post(
        `/admin/collaborator-scholarships/${selectedScholarship?.id}/reject`,
      );
      toast.success(`Bolsa negada!`);
      rejectDialogRef.current?.hide();
      loadData();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro ao negar bolsa.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <FilterDrawer
        ref={filterRef}
        columns={columns.filter((column) => !column.hide && column.field)}
        yupSchema={CollaboratorScholarshipFilterSchema}
      >
        <Filters formRef={filterRef.current?.getFormRef()} />
      </FilterDrawer>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container justify="space-between" alignItems="center">
            <Typography variant="h5">Bolsas de Colaboradores</Typography>

            <LinkButton
              to={`${url}/novo`}
              startIcon={<AddIcon />}
              color="primary"
              variant="contained"
              size="small"
            >
              Nova
            </LinkButton>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Table
                autoHeight
                rows={data}
                page={info.current_page - 1}
                pageSize={info.per_page}
                rowCount={info.total}
                columns={columns}
                loading={paginationLoading}
                pagination
                paginationMode="server"
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onFilterClick={filterRef.current?.open}
                canSearch
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ConfirmDialog
        ref={beneficiaryDialogRef}
        title="Enviar pedido de confirmação?"
        description="Será enviado um email à beneficiária para que seja confirmado que a pessoa é uma colaboradora. Certifique-se que a beneficiária tenha 1 ou mais emails cadastrados com notificações ativas!"
        confirmColor="primary"
        onConfirm={handleBeneficiaryConfirm}
        loading={loading}
      />

      <ConfirmDialog
        ref={mailDialogRef}
        title="Enviar voucher?"
        description="Será enviado um email à pessoa com o voucher da bolsa em anexo e um email para a parceira para orçamento, caso a parceira esteja configurada como orçamento."
        confirmColor="primary"
        onConfirm={handleMailConfirm}
        loading={loading}
      />

      <ConfirmDialog
        ref={approveDialogRef}
        title="Aprovar Bolsa?"
        description="Será enviado um email à pessoa com o voucher da bolsa em anexo e um email para a parceira para orçamento, caso a parceira esteja configurada como orçamento."
        confirmColor="primary"
        onConfirm={handleApproveConfirm}
        loading={loading}
      />

      <ConfirmDialog
        ref={rejectDialogRef}
        title="Negar Bolsa?"
        description="Ao negar a bolsa será enviado um e-mail para a pessoa notificando a rejeição."
        confirmColor="secondary"
        onConfirm={handleRejectConfirm}
        loading={loading}
      />
    </Container>
  );
};

export default List;
